import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ExternalLink } from "@/components/OptionalLink";
import { OptionalTooltip } from "@/components/OptionalTooltip";

type CellPrimitive = string | boolean | number | React.ReactNode;
type CellWithTooltip = { value: CellPrimitive; tooltip: React.ReactNode };
type CellFunc = (row: number, col: number) => CellPrimitive | CellWithTooltip;
type CellValue = CellPrimitive | CellWithTooltip | CellFunc;

const table: CellValue[][] = [
  [
    <span key="feature" className="text-xs text-muted-foreground">
      Comparing 2 vCPU, 4GB RAM and 40GB server
    </span>,
    "DollarDeploy + Hetzner",
    "Vercel, Netlify",
    "Cloud Provider (AWS)",
    "Dokploy or Coolify + Hetzner"
  ],
  [
    "Base pricing (per month)",
    () => ({
      value: 5 /* $5 DollarDeploy */ + 3.79 /* $3.79 Hetzner */,
      tooltip: <span>DollarDeploy $5 + Hetzner $3.79</span>
    }),
    () => ({
      value: 20,
      tooltip: <span>Free tier are only for non-commercial usage</span>
    }),
    () => ({
      value: 29.784 + 0.08 * 40,
      tooltip: <span>t3a.medium instance with 40GB EBS disk</span>
    }),
    () => ({
      value: 7 /* $7 Dokploy, $5 Coolify */ + 3.79 /* $3.79 Hetzner */,
      tooltip: <span>Dokploy $7 + Hetzner $3.79</span>
    })
  ],
  ["Included traffic", "20 TB", "1 TB", "0 TB", "20 TB"],
  ["Extra traffic price", "$0.01/TB", "$0.15/GB", "$0.09/GB", "$0.01/TB"],
  [
    {
      value: "Pay per request",
      tooltip:
        "Serverless providers like Vercel or Netlify provide some free requests limit and you need to pay to go over this limit."
    },
    "$0",
    "Can reach $600/month",
    "Expensive at scale",
    "$0"
  ],
  ["Ease of use", true, true, "Complicated", "Complicated"],
  [
    "Next.js, Go, Rust, Java, PHP support",
    "Native",
    "Native",
    "Limited with Amplify, complex with ECS/EKS",
    "With Docker"
  ],
  ["Server Monitoring¹", true, true, true, true],
  ["Logs²", "30 days", "1…7 days", true, true],
  ["Backups³", "One click", "Manual", "Manual", "Manual"]
];

const renderValue = (value: CellValue, row: number, col: number) => {
  let val: CellPrimitive | CellWithTooltip | undefined = undefined;
  if (typeof value === "function") {
    val = value(row, col);
  } else {
    val = value;
  }

  let prefix = "";
  const suffix = "";
  let tooltip: React.ReactNode | undefined = undefined;

  if (val && typeof val === "object" && "tooltip" in val && "value" in val) {
    tooltip = val.tooltip;
    val = val.value;
  }

  if (typeof val === "number") {
    val = val.toFixed(2);
    prefix = "$";
  }

  return (
    <span className="text-sm flex items-center gap-2 justify-center">
      <OptionalTooltip tooltip={tooltip}>
        {typeof val === "boolean" ? (
          val === true ? (
            <span className="flex items-center">
              {prefix}
              <Check className="h-5 w-5 text-green-500" />
              {suffix}
            </span>
          ) : (
            <span className="flex items-center">
              {prefix}
              <X className="h-5 w-5 text-red-500" />
              {suffix}
            </span>
          )
        ) : val === undefined ? (
          <span className="flex items-center">
            {prefix}
            <span className="text-muted-foreground">-</span>
            {suffix}
          </span>
        ) : (
          <span className="flex items-center">
            {prefix}
            {val as React.ReactNode}
            {suffix}
          </span>
        )}
      </OptionalTooltip>
    </span>
  );
};

export const Comparison = () => {
  return (
    <section id="comparison" className="container space-y-8 md:space-y-16">
      <div className="text-center space-y-4">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
          <span className="gradient-light dark:gradient-dark">Compare</span> with alternatives
        </h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto text-balance">
          Avoid surprise bills,{" "}
          <ExternalLink
            className="link"
            href="https://docs.dollardeploy.com/blog/self-host-next-js-apps/"
          >
            serverless limitations
          </ExternalLink>{" "}
          and complex DevOps setups by{" "}
          <ExternalLink
            className="link"
            href="https://docs.dollardeploy.com/blog/self-host-next-js-apps/"
          >
            self-hosting
          </ExternalLink>{" "}
          your apps on your own servers.
        </p>
      </div>

      {/* Table Container */}
      <div>
        <table className="Responsive w-full border-separate border-spacing-0">
          <tbody>
            {table.map((feature, row) => {
              const highlight = 1;
              const last = feature.length - 1;
              const lastRow = table.length - 1;

              return (
                <tr
                  key={row}
                  className={cn(
                    "hover:bg-muted/30 transition-colors",
                    row === 0 && "hidden md:table-row border-b-2 border-border font-bold"
                  )}
                >
                  {feature.map((value, index) => (
                    <td
                      key={index}
                      data-label={table[0][index]}
                      className={cn(
                        "border-border px-6 py-4",
                        "md:w-1/5",
                        "text-right md:text-center",
                        row === 0 && "bg-muted/50",
                        index === 0 && "md:border-l-2",
                        index === last && "md:border-r-2",
                        row === lastRow && "md:border-b-2",
                        row === 0 && "border-b-2",
                        row === 0 && index === 0 && "rounded-tl-lg border-t-2 border-l-2",
                        row === 0 && index === last && "rounded-tr-lg border-t-2 border-r-2",
                        row === 0 && index !== 0 && index !== last && "border-t-2",
                        index === highlight &&
                          row === 0 &&
                          "border-t-2 border-l-2 border-r-2 border-l-blue-500/70 border-r-blue-500/70",
                        index === highlight &&
                          row !== 0 &&
                          "border-b-2 border-t-2 md:border-b-0 md:border-t-0 md:border-l-2 md:border-r-2 border-blue-500/70",
                        index !== highlight && index !== 0 && "md:border-r-2",
                        index === highlight && row !== 0 ? "bg-blue-500/10" : undefined,
                        index === highlight &&
                          row === lastRow &&
                          "border-b-2 md:border-b-2 border-b-border"
                      )}
                    >
                      {renderValue(value, row, index)}
                    </td>
                  ))}

                  {/** Empty cell with actions and hidden for responsive view */}
                  <td className="px-6 py-4 text-center hidden"></td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <p className="pl-2 text-sm text-muted-foreground pt-2 flex flex-col gap-2">
          <span>¹ Monitoring in Dokploy only available in a cloud version.</span>
          <span>² For AWS, you need to use CloudWatch or S3 for logs.</span>
          <span>
            ² For Vercel and Netlify, you need custom solutions to backup data, and you need a
            custom Object Storage (S3) destination for Dokploy
          </span>
        </p>
      </div>
    </section>
  );
};
